/** Knowledge base — Step 33. Agent surface via apiFetch/apiSend; the portal
 *  and public surfaces have their own helpers (three auth domains, three
 *  helper pairs — never crossed). */
import { apiFetch, apiSend } from '../../lib/api';

export interface UploadedFile {
  id: string;
  url: string;
  filename: string;
  contentType: string;
  size: number;
}

/**
 * Upload one file. This is the one place a bare `fetch` is correct on the
 * agent side: apiFetch/apiSend serialise JSON, and a multipart body must
 * reach the network untouched with the browser setting its own boundary.
 * The 401 handling those helpers provide is reproduced here deliberately.
 */
export async function uploadFile(
  file: File,
  owner?: { ownerType: string; ownerId: string },
  purpose = 'kb_image',
): Promise<{ ok: boolean; body?: UploadedFile; error?: string }> {
  const form = new FormData();
  form.append('file', file);
  // The PURPOSE carries the cap and the allowlist — a KB screenshot and a
  // ticket attachment are different policies, not different routes.
  form.append('purpose', purpose);
  if (owner) {
    form.append('ownerType', owner.ownerType);
    form.append('ownerId', owner.ownerId);
  }
  // Bare fetch on purpose: apiFetch/apiSend serialise JSON, and a multipart
  // body must reach the network untouched so the browser can set its own
  // boundary header. The 401 redirect those helpers give is reproduced below.
  const res = await fetch('/api/media', { method: 'POST', body: form, credentials: 'same-origin' });
  if (res.status === 401) {
    window.location.assign(`/login?returnTo=${encodeURIComponent(window.location.pathname)}`);
    return { ok: false, error: 'Signed out' };
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) return { ok: false, error: body?.error ?? 'Upload failed' };
  return { ok: true, body };
}

export const KB_VISIBILITY = ['public', 'signed_in', 'agents'] as const;
export type KbVisibility = (typeof KB_VISIBILITY)[number];

/** Labels mirror lib/kbVisibility.ts on the server — the copy lives next to
 *  the rule there; this is the client half of the same wording. */
export const KB_VISIBILITY_LABELS: Record<KbVisibility, { label: string; help: string }> = {
  public: {
    label: 'Anyone',
    help: 'Visible on the public help site without signing in. Never put internal runbooks here.',
  },
  signed_in: {
    label: 'Signed-in people',
    help: 'Anyone who can sign in to the portal — staff and requesters, but not the public.',
  },
  agents: { label: 'Agents only', help: 'Internal. Never shown on the portal or the public site.' },
};

export interface KbFolder {
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  visibility: KbVisibility;
  published: number;
  drafts?: number;
}
export interface KbCategory {
  id: string;
  name: string;
  description: string | null;
  folders: KbFolder[];
}
export const ARTICLE_TYPES = ['permanent', 'workaround'] as const;
export type KbArticleType = (typeof ARTICLE_TYPES)[number];
export const ARTICLE_TYPE_LABELS: Record<KbArticleType, string> = {
  permanent: 'Permanent',
  workaround: 'Workaround',
};

export interface KbArticleRow {
  id: string;
  title: string;
  slug: string;
  status: 'draft' | 'published';
  folderId: string;
  views: number;
  helpful: number;
  notHelpful: number;
  insertedCount: number;
  articleType: KbArticleType;
  authorName: string | null;
  folderName: string;
  updatedAt: number;
  /** Step 122 — when to look at this again; null = nobody has said. */
  reviewAt?: number | null;
}
export interface KbVersionRow {
  version: number;
  title: string;
  savedByName: string | null;
  note: string | null;
  createdAt: number;
  status: string;
  size: number;
}
export interface KbVersion extends KbVersionRow {
  bodyHtml: string | null;
  keywords: string | null;
  articleType: KbArticleType;
}
export interface KbArticleDetail extends KbArticleRow {
  bodyHtml: string | null;
  keywords: string | null;
  folderVisibility: KbVisibility;
  categoryId: string;
  authorId: string | null;
  authorName: string | null;
  createdAt: number;
  publishedAt: number | null;
}

export const kbApi = {
  tree: () => apiFetch<{ categories: KbCategory[] }>('/api/kb/tree'),
  articles: (params: { folder?: string; status?: string; q?: string }) => {
    const qs = new URLSearchParams();
    if (params.folder) qs.set('folder', params.folder);
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    const s = qs.toString();
    return apiFetch<{ entries: KbArticleRow[] }>(`/api/kb/articles${s ? `?${s}` : ''}`);
  },
  article: (id: string) => apiFetch<KbArticleDetail>(`/api/kb/articles/${id}`),
  /** Step 122 — version history. */
  versions: (id: string) => apiFetch<{ entries: KbVersionRow[] }>(`/api/kb/articles/${id}/versions`),
  version: (id: string, version: number) => apiFetch<KbVersion>(`/api/kb/articles/${id}/versions/${version}`),
  restoreVersion: (id: string, version: number) =>
    apiSend<{ ok: true; version: number }>(`/api/kb/articles/${id}/versions/${version}/restore`, 'POST', {}),
  reviewDue: () =>
    apiFetch<{
      entries: { id: string; title: string; slug: string; reviewAt: number; folderName: string }[];
      now: number;
    }>('/api/kb/articles/review-due'),
  createCategory: (body: { name: string; description?: string }) =>
    apiSend<{ id: string }>('/api/kb/categories', 'POST', body),
  updateCategory: (id: string, body: { name?: string; description?: string }) =>
    apiSend(`/api/kb/categories/${id}`, 'PATCH', body),
  deleteCategory: (id: string) => apiSend(`/api/kb/categories/${id}`, 'DELETE'),
  createFolder: (body: { categoryId: string; name: string; visibility?: KbVisibility }) =>
    apiSend<{ id: string }>('/api/kb/folders', 'POST', body),
  updateFolder: (id: string, body: { name?: string; visibility?: KbVisibility; categoryId?: string }) =>
    apiSend(`/api/kb/folders/${id}`, 'PATCH', body),
  deleteFolder: (id: string) => apiSend(`/api/kb/folders/${id}`, 'DELETE'),
  createArticle: (body: { folderId: string; title: string; bodyHtml?: string; status?: string }) =>
    apiSend<{ id: string }>('/api/kb/articles', 'POST', body),
  updateArticle: (
    id: string,
    body: {
      folderId?: string;
      title?: string;
      bodyHtml?: string;
      status?: string;
      keywords?: string;
      articleType?: KbArticleType;
      authorId?: string | null;
      /** Step 122 */
      reviewAt?: number | null;
      expectedUpdatedAt?: number;
    },
  ) => apiSend(`/api/kb/articles/${id}`, 'PATCH', body),
  deleteArticle: (id: string) => apiSend(`/api/kb/articles/${id}`, 'DELETE'),
};
